import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Skeleton } from './ui/skeleton';
import { API, authFetch } from '../lib/api';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const d = new Date(String(dateString).replace(' ', 'T'));
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getInitials = (name = '') =>
  name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const RelatedCard = ({ article }) => (
  <Link
    to={`/article/${article.article_id}`}
    data-testid={`read-next-card-${article.article_id}`}
    className="group flex flex-col gap-3 p-5 rounded-xl border border-border bg-paper hover:border-accent/40 hover:shadow-sm transition-all"
  >
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Avatar className="w-5 h-5">
        <AvatarImage src={article.author_picture} alt={article.author_name} />
        <AvatarFallback className="text-[10px] bg-muted">
          {getInitials(article.author_name)}
        </AvatarFallback>
      </Avatar>
      <span className="truncate">{article.author_name}</span>
    </div>

    <h3
      className="text-lg font-semibold leading-snug line-clamp-3 group-hover:text-accent transition-colors"
      style={{ fontFamily: 'Fraunces, serif' }}
    >
      {article.title}
    </h3>

    {article.subtitle && (
      <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
        {article.subtitle}
      </p>
    )}

    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto">
      <span>{formatDate(article.published_at || article.created_at)}</span>
      <span className="flex items-center gap-1">
        <Clock className="w-3 h-3" />
        {article.reading_time} min
      </span>
    </div>
  </Link>
);

export const ReadNext = ({ articleId }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!articleId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/articles/${articleId}/related?limit=3`);
        if (!res.ok) {
          if (!cancelled) setItems([]);
          return;
        }
        const data = await res.json();
        if (!cancelled) setItems(data);
      } catch (e) {
        console.error('Failed to fetch related articles:', e);
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [articleId]);

  if (items && items.length === 0) return null;

  return (
    <section
      className="mt-12 pt-10 border-t border-border"
      data-testid="read-next-section"
      aria-labelledby="read-next-heading"
    >
      <h2
        id="read-next-heading"
        className="text-2xl sm:text-3xl font-bold mb-6"
        style={{ fontFamily: 'Fraunces, serif' }}
      >
        Read next
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {items === null
          ? Array(3).fill(0).map((_, i) => (
              <div key={`sk-${i}`} className="p-5 rounded-xl border border-border bg-paper">
                <Skeleton className="h-4 w-24 mb-3" />
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-6 w-3/4 mb-4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))
          : items.map(a => <RelatedCard key={a.article_id} article={a} />)}
      </div>
    </section>
  );
};

export default ReadNext;
